import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/** Atributo do carrinho (e do pedido) com o código da vendedora. */
export const SELLER_ATTRIBUTE = 'Código da vendedora';

/**
 * Cynthia Pamplona — campo "Código da vendedora" da sacola.
 *
 * Confere o código na lista de vendedoras (metafield da loja), grava no atributo do carrinho e envia ao
 * carrinho junto com os cupons já aplicados: a Shopify aplica sozinha o maior desconto, e o código da
 * vendedora continua no carrinho (e no pedido) mesmo quando o cupom vence.
 *
 * @typedef {Object} SellerCodeRefs
 * @property {HTMLInputElement} [input] - Campo do código (ausente quando já há código aplicado).
 * @property {HTMLElement} [error] - Mensagem de código não encontrado.
 *
 * @extends {Component<SellerCodeRefs>}
 */
class CpSellerCode extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @returns {string[]} */
  get #validCodes() {
    try {
      const codes = JSON.parse(this.dataset.codes || '[]');
      return Array.isArray(codes) ? codes.map((code) => String(code).toLowerCase()) : [];
    } catch {
      return [];
    }
  }

  /** @returns {string[]} */
  get #couponCodes() {
    return (this.dataset.couponCodes || '').split(',').map((code) => code.trim()).filter(Boolean);
  }

  /**
   * Aplica o código digitado.
   * @param {SubmitEvent} event
   */
  apply = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const { input, error } = this.refs;
    if (!input) return;

    const code = input.value.trim().toLowerCase();
    if (!code) return;

    if (!this.#validCodes.includes(code)) {
      error?.classList.remove('hidden');
      this.dispatchEvent(new CartErrorEvent({ error: 'Invalid seller code', code: 'VALIDATION_CUSTOM' }));
      return;
    }

    error?.classList.add('hidden');
    await this.#update([...this.#couponCodes, code], code);
  };

  /**
   * Remove o código da vendedora (mantém os cupons).
   * @param {MouseEvent} event
   */
  remove = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    await this.#update(this.#couponCodes, '');
  };

  /**
   * @param {string[]} discountCodes - Todos os códigos que devem ficar no carrinho.
   * @param {string} sellerCode - Código da vendedora a gravar ('' remove).
   */
  async #update(discountCodes, sellerCode) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    const deferredPromise = CartDiscountUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: discountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            discount: discountCodes.join(','),
            attributes: { [SELLER_ATTRIBUTE]: sellerCode },
            sections: [sectionId],
          }),
        }),
        signal: abortController.signal,
      });
      const data = await response.json();
      deferredPromise.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data) });
      await morphSection(sectionId, data.sections[sectionId], {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({ error: error.message || 'Failed to update seller code', code: 'SERVICE_UNAVAILABLE' })
        );
      }
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }
}

if (!customElements.get('cp-seller-code')) {
  customElements.define('cp-seller-code', CpSellerCode);
}
